import {
  canUseBackendProxy, disableBackendProxy, enableBackendProxy, getBackendBaseUrl,
  getBackendDirectUrl, getBackendProxyPath, isUsingBackendProxy,
} from './axios';

// Prueba de conexión al empezar el test. Hay redes de empresa que dejan cargar la
// página (pedido chico) pero bloquean los pedidos grandes: así una candidata
// respondió el test completo sin que nada llegara al servidor. Se manda un cuerpo
// del tamaño de un guardado real de avance (~60 KB) y se exige la respuesta exacta
// del back. Si la vía en uso falla y la otra (proxy del front ↔ directa) funciona,
// el resto del test va por esa desde el principio.

const PAYLOAD_BYTES = 64 * 1024;
const TIMEOUT_MS = 15000;

// ok: funciona la vía en uso · alternate: funciona solo la otra vía (y queda en
// uso) · blocked: no funciona ninguna · unknown: el back respondió algo inesperado
// (por ejemplo, el endpoint aún no está desplegado); no se puede concluir que la
// red bloquee, así que no se frena a la persona.
export type ConnectionStatus = 'ok' | 'alternate' | 'blocked' | 'unknown';

type ProbeResult = 'ok' | 'failed' | 'unexpected';

// fetch y no axios: interesa distinguir "la red cortó el pedido" de "el back
// respondió", y los interceptores de axios mezclan los dos casos.
const probe = async (base: string | undefined, viaProxy: boolean): Promise<ProbeResult> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${base || ''}/collaborator_tests/connection-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(viaProxy ? { 'X-GrowMe-Via': 'proxy' } : {}) },
      body: JSON.stringify({ padding: 'x'.repeat(PAYLOAD_BYTES) }),
      signal: controller.signal,
    });
    // Un proxy de empresa que bloquea suele responder él mismo (403/407 o 5xx de
    // pasarela): cuenta como bloqueo, no como respuesta del back.
    if ([403, 407, 502, 503, 504].includes(response.status)) return 'failed';
    const data = await response.json().catch(() => null);
    if (response.ok && data?.ok === true && data?.receivedBytes === PAYLOAD_BYTES) return 'ok';
    return 'unexpected';
  } catch (error) {
    return 'failed';
  } finally {
    clearTimeout(timer);
  }
};

export const checkConnection = async (): Promise<ConnectionStatus> => {
  const viaProxy = isUsingBackendProxy();
  const first = await probe(getBackendBaseUrl(), viaProxy);
  if (first === 'ok') return 'ok';
  if (first === 'unexpected') return 'unknown';
  if (!canUseBackendProxy()) return 'blocked';

  // La vía en uso no pasa: se prueba la otra (proxy ↔ directa).
  const second = viaProxy
    ? await probe(getBackendDirectUrl(), false)
    : await probe(getBackendProxyPath(), true);
  if (second === 'ok') {
    if (viaProxy) disableBackendProxy(); else enableBackendProxy();
    return 'alternate';
  }
  return second === 'unexpected' ? 'unknown' : 'blocked';
};
